import React, { useContext } from 'react';

import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import { UserDataContext } from 'lane-shared/contexts';
import { ContentNotificationType } from 'lane-shared/types/content/DraftContent';
import type { PillColor } from 'lane-shared/types/general/pill';

import Pill from 'components/general/Pill';
import tokens from '@viewthespace/style-dictionary/assets/json/tokens.json';

import IconButton from '../../general/IconButton';

import { Flex } from 'components/layout';
import { Button, Tooltip } from 'design-system-web';

import { H5, M } from 'components/typography';

import styles from './NotificationStatusCard.scss';
import { MultiLanguageText } from 'components/general/MultiLanguageText';
import { Channel } from 'packages/lane-shared/types/ChannelType';
import { useNotificationTypeSelectorEnabled } from 'react-hooks';
import { getNotificationDeliveryType } from 'components/lane/NotificationDeliveryTypeCheckboxGroup';

export enum NotificationStatus {
  SENT,
  SCHEDULED,
  NO_RECIPIENTS,
}

const TRANSLATION_KEYS = {
  notificationMessage:
    'web.components.lane.DraftContent.DraftContentNotifications.notificationMessage',
  deleteButton: 'web.components.cards.DatePickerCard.deleteButton',
  noRecipients:
    'web.components.lane.DraftContent.DraftContentNotifications.noRecipients',
  noRecipientsTooltip:
    'web.components.lane.DraftContent.DraftContentNotifications.noRecipientsTooltip',
};

type PillConfig = {
  color: PillColor;
  textKey: string;
  iconName: string;
  iconColor: string;
};

const STATUS_PILL_CONFIG: Record<NotificationStatus, PillConfig> = {
  [NotificationStatus.SENT]: {
    color: 'green',
    textKey: 'Sent',
    iconName: 'circle',
    iconColor: tokens.color.general.green.secondary,
  },
  [NotificationStatus.SCHEDULED]: {
    color: 'honey',
    textKey: 'Scheduled',
    iconName: 'circle',
    iconColor: tokens.color.general.yellow.secondary,
  },
  [NotificationStatus.NO_RECIPIENTS]: {
    color: 'gray',
    textKey: TRANSLATION_KEYS.noRecipients,
    iconName: 'circle',
    iconColor: tokens.color.general.gray.secondary,
  },
};

type Props = {
  channel?: Channel;
  notification: ContentNotificationType;
  status: NotificationStatus;
  timezone?: string | null;
  removeNotification?: (notification: ContentNotificationType) => void;
};

export function NotificationStatusCard({
  channel,
  notification,
  status,
  timezone,
  removeNotification,
}: Props) {
  const { sendAt } = notification;

  const { t } = useTranslation();
  const { user } = useContext(UserDataContext);
  const isNotificationTypeSelectorEnabled = useNotificationTypeSelectorEnabled();

  const config = STATUS_PILL_CONFIG[status];
  const pillProps = {
    color: config.color,
    text: t(config.textKey),
    iconName: config.iconName,
    iconColor: config.iconColor,
  };
  const deliveryTypeLabel = getNotificationDeliveryType(
    notification.deliveryTypes,
    t
  );

  let date = DateTime.fromJSDate(sendAt!);

  if (user?.locale) date = date.setLocale(user.locale);

  if (timezone) date = date.setZone(timezone);

  return (
    <div className={styles.existingNotificationContainer}>
      <Flex
        direction="row"
        justify="space-between"
        align="center"
        className={styles.notificationTitleContainer}
      >
        <Flex direction="row" align="center">
          <IconButton icon="Bell" size="small" className={styles.iconButton} />
          <H5 className={styles.notificationDate}>
            {date.toFormat('MMMM dd, yyyy')}
          </H5>
          <M className={styles.notificationTime}>
            {`${date.toFormat('hh:mm a')} (${date.toFormat('ZZZZ')})`}
          </M>
        </Flex>
        <Flex direction="row" align="center" gap={2}>
          {isNotificationTypeSelectorEnabled && deliveryTypeLabel && (
            <Pill
              size="medium"
              color="blue"
              text={deliveryTypeLabel}
              className={styles.customPill}
            />
          )}
          {status === NotificationStatus.NO_RECIPIENTS ? (
            <Tooltip
              placement="bottom"
              TooltipComponent={t(TRANSLATION_KEYS.noRecipientsTooltip)}
            >
              <Pill size="medium" className={styles.customPill} {...pillProps} />
            </Tooltip>
          ) : (
            <Pill size="medium" className={styles.customPill} {...pillProps} />
          )}
        </Flex>
      </Flex>
      <MultiLanguageText model={notification} channel={channel} column="title">
        {({ labelMaker, textMaker }) => {
          return (
            <Flex
              direction="column"
              justify="flex-start"
              className={styles.notificationMessageWrapper}
            >
              {labelMaker({ label: t(TRANSLATION_KEYS.notificationMessage) })}
              {textMaker()}
            </Flex>
          );
        }}
      </MultiLanguageText>
      {status === NotificationStatus.SCHEDULED && (
        <Flex direction="row" justify="flex-start">
          <Button
            testId="removeScheduled"
            className={styles.removeButton}
            onClick={() =>
              removeNotification && removeNotification(notification)
            }
          >
            {t(TRANSLATION_KEYS.deleteButton)}
          </Button>
        </Flex>
      )}
    </div>
  );
}
